import { useState } from 'react';
import { instances as instancesApi, messages as messagesApi } from '../api/resources.js';
import { Card, CopyButton, Empty, ErrorState, Loading, StatusPill } from '../components/ui.js';
import { useToast } from '../components/toast.js';
import { formatDateTime, formatPhone } from '../lib/format.js';
import { useAction, useAsync } from '../lib/hooks.js';

/** Message Explorer (PRD §38) and test send (PRD §26). */
export function MessagesPage() {
  const toast = useToast();
  const [filter, setFilter] = useState({ instanceId: '', direction: '', status: '' });
  const [send, setSend] = useState({ instanceId: '', to: '', text: '' });

  const instances = useAsync(() => instancesApi.list(), []);
  const list = useAsync(
    () =>
      messagesApi.list({
        instanceId: filter.instanceId || undefined,
        direction: filter.direction || undefined,
        status: filter.status || undefined,
        limit: '100',
      }),
    [filter.instanceId, filter.direction, filter.status],
  );

  const connected = (instances.data?.data ?? []).filter(
    (instance) => instance.status === 'CONNECTED' || instance.status === 'DEGRADED',
  );

  const sendMessage = useAction(async () => {
    const instanceId = send.instanceId || connected[0]?.id;
    if (!instanceId) return;
    const message = await messagesApi.sendText(instanceId, send.to, send.text);
    toast.success('Mensagem enviada', `ID: ${message.id}`);
    setSend((current) => ({ ...current, text: '' }));
    list.reload();
  });

  return (
    <>
      <Card
        title="Enviar mensagem de teste"
        description={
          connected.length === 0
            ? 'Conecte uma instância para poder enviar.'
            : 'O envio usa exatamente o mesmo endpoint público da API.'
        }
      >
        <form
          className="form-row"
          onSubmit={(event) => {
            event.preventDefault();
            void sendMessage.run();
          }}
        >
          <div>
            <label htmlFor="send-instance">Instância</label>
            <select
              id="send-instance"
              value={send.instanceId || connected[0]?.id || ''}
              onChange={(event) => setSend((c) => ({ ...c, instanceId: event.target.value }))}
              disabled={connected.length === 0}
            >
              {connected.map((instance) => (
                <option key={instance.id} value={instance.id}>
                  {instance.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="send-to">Destinatário</label>
            <input
              id="send-to"
              value={send.to}
              onChange={(event) => setSend((c) => ({ ...c, to: event.target.value }))}
              placeholder="5511999999999"
              required
              disabled={connected.length === 0}
            />
          </div>
          <div style={{ flex: '2 1 240px' }}>
            <label htmlFor="send-text">Mensagem</label>
            <input
              id="send-text"
              value={send.text}
              onChange={(event) => setSend((c) => ({ ...c, text: event.target.value }))}
              placeholder="Olá! Esta é uma mensagem de teste."
              required
              disabled={connected.length === 0}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={sendMessage.pending || connected.length === 0}
          >
            {sendMessage.pending ? 'Enviando…' : 'Enviar'}
          </button>
        </form>

        {sendMessage.error != null && (
          <div style={{ marginTop: 12 }}>
            <ErrorState error={sendMessage.error} />
          </div>
        )}
      </Card>

      {/*
        A ressalva fica na tela, e não só no contrato, porque quem olha esta lista está
        tentando responder "chegou?" — e a resposta honesta é que `Enviada` não distingue
        "não entregue" de "entregue sem recibo". Um cliente que descobre isso sozinho,
        depois de reenviar mensagem para gente que já tinha recebido, descobre tarde.
      */}
      <Card
        title="Histórico"
        description="Filtre por instância, direção ou status. Enviada significa aceita pelo WhatsApp; Entregue e Lida dependem de o WhatsApp devolver o recibo, o que nem sempre acontece."
        bodyless
        actions={
          <div className="row">
            <select
              aria-label="Instância"
              value={filter.instanceId}
              onChange={(event) => setFilter((c) => ({ ...c, instanceId: event.target.value }))}
            >
              <option value="">Todas as instâncias</option>
              {(instances.data?.data ?? []).map((instance) => (
                <option key={instance.id} value={instance.id}>
                  {instance.name}
                </option>
              ))}
            </select>
            <select
              aria-label="Direção"
              value={filter.direction}
              onChange={(event) => setFilter((c) => ({ ...c, direction: event.target.value }))}
            >
              <option value="">Entrada e saída</option>
              <option value="OUTBOUND">Enviadas</option>
              <option value="INBOUND">Recebidas</option>
            </select>
            <select
              aria-label="Status"
              value={filter.status}
              onChange={(event) => setFilter((c) => ({ ...c, status: event.target.value }))}
            >
              <option value="">Qualquer status</option>
              <option value="SENT">Enviada</option>
              <option value="DELIVERED">Entregue</option>
              <option value="READ">Lida</option>
              <option value="FAILED">Falhou</option>
            </select>
          </div>
        }
      >
        {list.loading ? (
          <Loading />
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={list.reload} />
        ) : list.data?.data.length === 0 ? (
          <Empty title="Nenhuma mensagem encontrada">
            <p className="muted">Ajuste os filtros ou envie uma mensagem de teste.</p>
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Direção</th>
                  <th>Tipo</th>
                  <th>Contato</th>
                  <th>Status</th>
                  <th>Request ID</th>
                </tr>
              </thead>
              <tbody>
                {(list.data?.data ?? []).map((message) => (
                  <tr key={message.id}>
                    <td className="subtle">{formatDateTime(message.createdAt)}</td>
                    <td>
                      <span className="pill pill-neutral">
                        {message.direction === 'OUTBOUND' ? '↑ enviada' : '↓ recebida'}
                      </span>
                    </td>
                    <td>{message.type}</td>
                    <td>{formatPhone(message.recipient)}</td>
                    <td>
                      <StatusPill status={message.status} label={message.status} />
                      {message.errorCode && <div className="subtle mono">{message.errorCode}</div>}
                    </td>
                    <td>
                      {message.requestId ? (
                        <div className="row">
                          <code className="subtle">{message.requestId.slice(0, 12)}…</code>
                          <CopyButton value={message.requestId} label="Copiar" />
                        </div>
                      ) : (
                        <span className="subtle">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
